'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import type { InboxNotification } from '@/types/notifications';

export type BrowserNotificationPermission = NotificationPermission | 'unsupported';

interface BrowserNotificationsOptions {
  enabled: boolean;
  notifications: InboxNotification[];
  isLoading: boolean;
  onOpen: (notification: InboxNotification) => Promise<void>;
}

/**
 * The window Notification API can only notify while this page is alive. It is still
 * useful for a background tab, but must not be confused with Web Push (which would
 * require a service worker and a push subscription).
 */
export function browserNotificationPermission(): BrowserNotificationPermission {
  if (
    typeof window === 'undefined'
    || !window.isSecureContext
    || !('Notification' in window)
  ) {
    return 'unsupported';
  }

  return window.Notification.permission;
}

export function useBrowserNotificationPermission(): {
  permission: BrowserNotificationPermission;
  requestPermission: () => Promise<BrowserNotificationPermission>;
} {
  const [permission, setPermission] = useState<BrowserNotificationPermission>(
    browserNotificationPermission,
  );

  useEffect(() => {
    const syncPermission = () => setPermission(browserNotificationPermission());
    window.addEventListener('focus', syncPermission);
    document.addEventListener('visibilitychange', syncPermission);
    return () => {
      window.removeEventListener('focus', syncPermission);
      document.removeEventListener('visibilitychange', syncPermission);
    };
  }, []);

  const requestPermission = useCallback(async (): Promise<BrowserNotificationPermission> => {
    if (browserNotificationPermission() === 'unsupported') return 'unsupported';
    const nextPermission = await window.Notification.requestPermission();
    setPermission(nextPermission);
    return nextPermission;
  }, []);

  return { permission, requestPermission };
}

/**
 * Mirrors newly-arrived unread inbox entries into native browser notifications.
 * The first settled snapshot is only a baseline: opening the app with an existing
 * backlog must never produce a burst of stale system notifications.
 */
export function useBrowserNotifications({
  enabled,
  notifications,
  isLoading,
  onOpen,
}: BrowserNotificationsOptions): void {
  const primed = useRef(false);
  const seenMessageIds = useRef(new Set<string>());

  useEffect(() => {
    if (!enabled) {
      primed.current = false;
      seenMessageIds.current.clear();
      return;
    }
    if (isLoading) return;

    if (!primed.current) {
      notifications.forEach((notification) => {
        seenMessageIds.current.add(notification.messageId);
      });
      primed.current = true;
      return;
    }

    const newUnread = notifications.filter((notification) => {
      if (seenMessageIds.current.has(notification.messageId)) return false;
      seenMessageIds.current.add(notification.messageId);
      return !notification.read;
    });

    // The in-app bell already communicates arrivals while the user is looking at the
    // page. Native notifications are reserved for a background tab to avoid duplicates.
    if (
      newUnread.length === 0
      || document.visibilityState !== 'hidden'
      || browserNotificationPermission() !== 'granted'
    ) {
      return;
    }

    newUnread.forEach((notification) => {
      try {
        const options: NotificationOptions = {
          icon: '/favicon.png',
          tag: `vertice-inbox:${notification.messageId}`,
        };
        if (notification.preview) options.body = notification.preview;

        const browserNotification = new window.Notification(notification.title, options);
        browserNotification.onclick = () => {
          browserNotification.close();
          window.focus();
          void onOpen(notification);
        };
      } catch {
        // Some mobile browsers expose Notification but only allow a service worker's
        // showNotification(). The inbox remains available, so this enhancement fails soft.
      }
    });
  }, [enabled, isLoading, notifications, onOpen]);
}
